import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Bell, ChevronRight } from 'lucide-react';
import useOrderStore from '../../../stores/orderStore';
import useUIStore from '../../../stores/uiStore';
import { ORDER_STATUS } from '../../../constants/orderStatus';
import TabBar from '../TabBar/TabBar';
import { useProductRanking } from '../../../../presentation/hooks/ranking/useInsightRanking';

/* ─── 라우트 → 헤더 타이틀 매핑 ─── */
const tabTitles = {
  '/dashboard/home': '대시보드',
  '/dashboard/rx': '처방전 관리',
  '/dashboard/patient': '환자관리',
  '/dashboard/adherence': '복약이행관리',
  '/dashboard/insight': '판매 인사이트　›　전체 현황',
  '/dashboard/marketing': '외부 프로모션',
  '/dashboard/ranking': '판매 순위',
  '/dashboard/orders': '주문 관리',
  '/dashboard/realtime': '실시간 현황',
  '/dashboard/settings': '약국 설정',
  '/dashboard/events/distributions': '지급내역',
  '/dashboard/events/history': '사용내역',
  '/dashboard/coupons/distribute': '이벤트 만들기',
  '/dashboard/coupons/distributions': '쿠폰 배포 현황',
  '/dashboard/points/status': '팜포인트 현황',
  '/dashboard/points/rules': '팜포인트 적립 기준',
  '/dashboard/points/paypoint': '팜페이포인트 현황',
  '/dashboard/points/distribute': '이벤트 만들기',
  '/dashboard/points/history': '포인트 이력',
  '/dashboard/target-groups': '타겟 그룹 관리',
  '/dashboard/settlement/home': '정산',
  '/dashboard/statistics': '통계',
};

// '2026-05-06' → '5/06' (Figma "주간 기준 5/06 ~ 5/12" 형식)
const weekDay = iso => {
  const [, m, d] = String(iso || '').split('-');
  return m && d ? `${Number(m)}/${d}` : '';
};

// 마지막 폴링 성공 시각 → '방금' | 'N분전' | 'N시간전'
const syncedAgo = (at, now) => {
  const min = Math.floor((now - at) / 60000);
  if (min < 1) return '방금';
  return min < 60 ? `${min}분전` : `${Math.floor(min / 60)}시간전`;
};

/* ko-KR: "26년 4월 27일 월요일 · 12:50" 포맷 */
const formatDateTime = date => {
  const yy = String(date.getFullYear()).slice(2);
  const mm = date.getMonth() + 1;
  const dd = date.getDate();
  const weekdays = [
    '일요일',
    '월요일',
    '화요일',
    '수요일',
    '목요일',
    '금요일',
    '토요일',
  ];
  const weekday = weekdays[date.getDay()];
  const hh = String(date.getHours()).padStart(2, '0');
  const min = String(date.getMinutes()).padStart(2, '0');
  return `${yy}년 ${mm}월 ${dd}일 ${weekday} · ${hh}:${min}`;
};

const Header = ({ onNotifClick }) => {
  const location = useLocation();
  const [promotionMode, setPromotionMode] = useState('active');
  const [insightView, setInsightView] = useState('전체 현황');
  useEffect(() => {
    const onMode = event => setPromotionMode(event.detail);
    window.addEventListener('promotion-mode', onMode);
    return () => window.removeEventListener('promotion-mode', onMode);
  }, []);
  useEffect(() => {
    const onView = event => setInsightView(event.detail);
    window.addEventListener('insight-view', onView);
    return () => window.removeEventListener('insight-view', onView);
  }, []);
  const promotionTitles = {
    active: '진행 중',
    review: '신규 캠페인 검토',
    done: '종료된 캠페인',
    history: '사용내역',
  };
  const title = tabTitles[location.pathname] || '대시보드';
  const isEventList = [
    '/dashboard/marketing',
    '/dashboard/events/distributions',
    '/dashboard/events/history',
  ].includes(location.pathname);
  const isInsight = location.pathname === '/dashboard/insight';
  // 랭킹 카드와 같은 쿼리(캐시 공유) — 기준 주(완료된 주)
  const { data: rankingMeta } = useProductRanking({ scope: isInsight ? 'national' : undefined, limit: 5 });
  const weekStart = weekDay(rankingMeta?.period?.start);
  const weekEnd = weekDay(rankingMeta?.period?.end);
  const lastSyncedAt = useOrderStore(state => state.lastSyncedAt);
  const isMarketing = location.pathname === '/dashboard/marketing';

  const notificationCount = useOrderStore(
    state => state.orders.filter(o => o.status === ORDER_STATUS.PENDING).length
  );

  const headerTabs = useUIStore(state => state.headerTabs);
  const headerActiveTab = useUIStore(state => state.headerActiveTab);
  const headerOnTabChange = useUIStore(state => state.headerOnTabChange);

  // 1분마다 시간 갱신
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="flex-shrink-0 bg-white">
      <header
        className={`flex box-border items-center gap-3 border-b border-zinc-200 px-6 ${isEventList || isInsight ? 'h-[54px] py-[15px]' : 'h-14 py-4'}`}
      >
        {/* Left: Title + Divider + Date·Time */}
        <div className="flex flex-1 items-center gap-4 min-w-0">
          {isInsight || isMarketing ? (
            <div className="flex items-center gap-1.5 whitespace-nowrap">
              <span className="text-14 font-semibold text-zinc-400">
                {isInsight ? '판매 인사이트' : '외부 프로모션'}
              </span>
              <ChevronRight size={18} className="text-zinc-400" />
              <span className="text-16 font-semibold text-zinc-700">
                {isInsight ? insightView : promotionTitles[promotionMode]}
              </span>
            </div>
          ) : (
            <p className="text-18 font-semibold text-zinc-700 leading-[24px] tracking-[-0.36px] whitespace-nowrap">
              {title}
            </p>
          )}
          <div className="w-px h-4 bg-zinc-200 shrink-0" aria-hidden="true" />
          <p className="text-12 font-medium text-zinc-500 whitespace-nowrap tabular-nums">
            {formatDateTime(now)}
            {isInsight && weekStart && weekEnd ? ` · 주간 기준 ${weekStart} ~ ${weekEnd}` : ''}
          </p>
        </div>

        {/* Right: Last Sync + Divider + Bell */}
        <div className="flex items-center gap-4 shrink-0">
          <p className="text-12 font-medium text-zinc-500 whitespace-nowrap">
            {lastSyncedAt ? `마지막 동기화 ${syncedAgo(lastSyncedAt, now)}` : ''}
          </p>
          <div className="w-px h-4 bg-zinc-200" aria-hidden="true" />
          <button
            type="button"
            onClick={onNotifClick}
            className="relative flex items-center justify-center w-[22px] h-[22px]"
            aria-label="알림"
          >
            <Bell size={22} className="text-zinc-900" strokeWidth={1.5} />
            {notificationCount > 0 && (
              <span className="absolute -top-px right-0 w-2 h-2 bg-[#FF3D00] border-2 border-white rounded-full" />
            )}
          </button>
        </div>
      </header>

      {headerTabs && (
        <TabBar
          tabs={headerTabs}
          activeTab={headerActiveTab}
          onTabChange={headerOnTabChange}
        />
      )}
    </div>
  );
};

export default Header;
